import React, { useState, useContext } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Button,
  CssBaseline,
  TextField,
  Box,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  Link
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Visibility, VisibilityOff } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
    backgroundColor: "#0f172a",
  },
  // Infinite tiled pattern with transparency (pattern.jpg or pattern.png in public)
  patternLayer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: "url(/pattern.jpg)",
    backgroundRepeat: "repeat",
    backgroundSize: "auto",
    opacity: 0.12,
    zIndex: 0,
    pointerEvents: "none",
  },
  content: {
    position: "relative",
    zIndex: 2,
    width: "100%",
    maxWidth: 420,
    padding: theme.spacing(3),
  },
  paper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(4, 3),
    borderRadius: 20,
    boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.08), 0 30px 60px -12px rgba(0, 0, 0, 0.6), 0 60px 120px -24px rgba(0, 0, 0, 0.5), 0 100px 200px -40px rgba(0, 0, 0, 0.45)",
    backgroundColor: "#1e3a5f",
    width: "100%",
    color: "#e2e8f0",
  },
  logo: {
    marginBottom: theme.spacing(2),
    maxWidth: 160,
    width: "100%",
    height: "auto",
    objectFit: "contain",
  },
  title: {
    fontWeight: 700,
    color: "#f1f5f9",
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    fontSize: "0.95rem",
    color: "#94a3b8",
    marginBottom: theme.spacing(3),
  },
  form: {
    width: "100%",
    marginTop: theme.spacing(1),
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: "rgba(255, 255, 255, 0.06)",
      "& fieldset": {
        borderColor: "rgba(255, 255, 255, 0.15)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(255, 255, 255, 0.3)",
        backgroundColor: "rgba(255, 255, 255, 0.08)",
      },
      "&.Mui-focused fieldset": {
        borderWidth: 2,
        borderColor: "#60a5fa",
      },
    },
    "& .MuiInputLabel-root": {
      color: "#ffffff !important",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#ffffff !important",
    },
    "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
      color: "#ffffff !important",
    },
    "& .MuiInputBase-input": {
      color: "#f1f5f9",
    },
    "& .MuiInputBase-input::placeholder": {
      color: "#ffffff !important",
      opacity: 0.95,
    },
  },
  inputLabel: {
    color: "#ffffff !important",
  },
  submit: {
    margin: theme.spacing(2.5, 0, 1.5),
    padding: theme.spacing(1.5),
    borderRadius: 12,
    textTransform: "none",
    fontWeight: 700,
    fontSize: "1rem",
    backgroundColor: "#3b82f6",
    color: "#fff",
    boxShadow: "0 4px 14px rgba(59, 130, 246, 0.4)",
    "&:hover": {
      backgroundColor: "#2563eb",
      boxShadow: "0 6px 20px rgba(59, 130, 246, 0.5)",
    },
  },
  registerLink: {
    marginTop: theme.spacing(2),
    textAlign: "center",
    width: "100%",
  },
  registerLinkText: {
    color: "#94a3b8",
    fontSize: "0.9rem",
    "&:hover": {
      color: "#93c5fd",
    },
  },
}));

const Login = () => {
  const classes = useStyles();

  const [user, setUser] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);

  const { handleLogin } = useContext(AuthContext);

  const handleChangeInput = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handlSubmit = (e) => {
    e.preventDefault();
    handleLogin(user);
  };

  return (
    <div className={classes.root}>
      <div className={classes.patternLayer} aria-hidden="true" />
      <CssBaseline />
      <Box className={classes.content}>
        <Paper elevation={0} className={classes.paper}>
          <img src="/logo.png" alt="AllExpress CRM" className={classes.logo} />
          <Typography component="h1" variant="h5" className={classes.title}>
            {i18n.t("login.title")}
          </Typography>

          <form className={classes.form} noValidate onSubmit={handlSubmit}>
            <TextField
              className={classes.textField}
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              label={i18n.t("login.form.email")}
              name="email"
              value={user.email}
              onChange={handleChangeInput}
              autoComplete="email"
              autoFocus
              InputLabelProps={{
                className: classes.inputLabel,
                style: { color: "#ffffff" },
              }}
              inputProps={{
                style: { color: "#f1f5f9" },
              }}
            />
            <TextField
              className={classes.textField}
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label={i18n.t("login.form.password")}
              id="password"
              value={user.password}
              onChange={handleChangeInput}
              autoComplete="current-password"
              type={showPassword ? "text" : "password"}
              InputLabelProps={{
                className: classes.inputLabel,
                style: { color: "#ffffff" },
              }}
              inputProps={{
                style: { color: "#f1f5f9" },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((e) => !e)}
                      edge="end"
                      style={{ color: "#94a3b8" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              className={classes.submit}
            >
              {i18n.t("login.buttons.submit")}
            </Button>
          </form>
        </Paper>
      </Box>
    </div>
  );
};

export default Login;
